import { useEffect, type ReactNode } from 'react';
import { B2B_CANDIDATE_RESUME_BUCKET } from '@core/supabase/storageConfig';
import { useSignedStorageObjectUrl } from '@core/hooks/useSignedStorageObjectUrl';

type Props = {
  /** Storage path inside the bucket (e.g. org-user-avatars/.../profile.jpg). */
  path: string | null | undefined;
  bucket?: string;
  className?: string;
  /** Shown while the signed URL is loading, if signing fails, or if the image errors. */
  fallback: ReactNode;
  onImageError?: () => void;
  /** Called when a signed URL could not be created (RLS/network). */
  onSignFailed?: () => void;
};

/**
 * Avatar / image from a **private** Supabase Storage bucket.
 * Public `/object/public/...` URLs return 400 for private buckets; this uses createSignedUrl.
 */
export function PrivateStorageAvatar({
  path,
  bucket = B2B_CANDIDATE_RESUME_BUCKET,
  className = '',
  fallback,
  onImageError,
  onSignFailed,
}: Props) {
  const { url, loading, error } = useSignedStorageObjectUrl(bucket, path);

  useEffect(() => {
    if (error) onSignFailed?.();
  }, [error, onSignFailed]);

  if (!path?.trim()) return <>{fallback}</>;
  if (loading || error || !url) return <>{fallback}</>;

  return (
    <img
      src={url}
      alt=""
      className={className}
      onError={() => onImageError?.()}
    />
  );
}
